<script setup>
import { useI18n } from 'vue-i18n';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import Spinner from 'shared/components/Spinner.vue';

// Quadro por agente: cada linha é um responsável de ciclo (applied_slas.
// assignee_id), com ciclos, cumpridos, perdidos, taxa e perdas por tipo.
defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const rowKey = row => (row.agent ? `agent-${row.agent.id}` : 'unassigned');
const agentName = row =>
  row.agent ? row.agent.name : t('SLA_REPORTS.AGENTS.UNASSIGNED');
</script>

<template>
  <div
    class="min-w-full shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6"
  >
    <div class="flex flex-col gap-1 mb-4">
      <span class="text-base font-medium text-n-slate-12">
        {{ $t('SLA_REPORTS.AGENTS.TITLE') }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{ $t('SLA_REPORTS.AGENTS.DESCRIPTION') }}
      </span>
    </div>
    <div
      class="grid content-center h-12 grid-cols-12 gap-4 px-6 py-0 bg-n-slate-2 rounded-md"
    >
      <TableHeaderCell
        :span="3"
        :label="$t('SLA_REPORTS.AGENTS.HEADER.AGENT')"
      />
      <TableHeaderCell
        :span="1"
        :label="$t('SLA_REPORTS.AGENTS.HEADER.CYCLES')"
      />
      <TableHeaderCell :span="1" :label="$t('SLA_REPORTS.AGENTS.HEADER.HIT')" />
      <TableHeaderCell
        :span="1"
        :label="$t('SLA_REPORTS.AGENTS.HEADER.MISSED')"
      />
      <TableHeaderCell
        :span="2"
        :label="$t('SLA_REPORTS.AGENTS.HEADER.HIT_RATE')"
      />
      <TableHeaderCell :span="1" :label="$t('SLA_REPORTS.AGENTS.HEADER.FRT')" />
      <TableHeaderCell :span="1" :label="$t('SLA_REPORTS.AGENTS.HEADER.NRT')" />
      <TableHeaderCell :span="1" :label="$t('SLA_REPORTS.AGENTS.HEADER.RT')" />
    </div>

    <div v-if="isLoading" class="flex items-center justify-center h-32">
      <Spinner />
      <span>{{ $t('SLA_REPORTS.LOADING') }}</span>
    </div>
    <div v-else-if="agents.length > 0">
      <div
        v-for="row in agents"
        :key="rowKey(row)"
        class="grid items-center content-center w-full h-14 grid-cols-12 gap-4 px-6 py-0 border-b last:border-b-0 border-n-weak text-sm text-n-slate-12"
      >
        <div class="flex items-center col-span-3 gap-2 min-w-0">
          <UserAvatarWithName v-if="row.agent" :user="row.agent" />
          <span v-else class="text-n-slate-11">{{ agentName(row) }}</span>
        </div>
        <span class="col-span-1">{{ row.total_cycles }}</span>
        <span class="col-span-1">{{ row.hit_cycles }}</span>
        <span class="col-span-1">{{ row.missed_cycles }}</span>
        <span class="col-span-2 font-medium">{{ row.hit_rate }}</span>
        <span class="col-span-1 text-n-slate-11">{{ row.frt_misses }}</span>
        <span class="col-span-1 text-n-slate-11">{{ row.nrt_misses }}</span>
        <span class="col-span-1 text-n-slate-11">{{ row.rt_misses }}</span>
      </div>
    </div>
    <div v-else class="flex items-center justify-center h-32 text-n-slate-11">
      {{ $t('SLA_REPORTS.AGENTS.NO_RECORDS') }}
    </div>
  </div>
</template>
